<template>
  <CustomModal ref="modal" :title="title" class="disbursement-modal">
    <div class="container">
      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
        <CustomSelectField v-if="canChoosePurchase" class="select" :content=inputs.purchaseId />
        <p v-else-if="purchase" class="recap">
          <span class="recap-supplier">{{ purchase.supplierName }}</span>
          <span class="recap-purchase">{{ purchaseLabel }}</span>
        </p>
        <p v-if="purchase" class="note">
          <template v-if="isEdit">Montant maximum pour {{ isRefund ? 'ce remboursement' : 'ce décaissement' }} : {{ formatAmount(maxAmount) }}</template>
          <template v-else>{{ isRefund ? 'Reste à rembourser' : 'Reste à payer' }} : {{ formatAmount(maxAmount) }}</template>
        </p>
        <div class="fields-row">
          <CustomTextField class="amount" :content=inputs.amount />
          <CustomTextField class="date" :content=inputs.date />
        </div>
        <div class="fields-row">
          <CustomSelectField class="payment-type" :content=inputs.paymentType />
          <CustomTextField class="reference" :content=inputs.reference />
        </div>
        <CustomTextField class="notes" :content=inputs.notes />
        <div class="button-container">
          <CustomButton class="button" @click="submitForm" :params=buttons.submit />
          <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
          <CustomButton v-if="isEdit" class="button remove" :is-secondary="true" @click="remove" :params=buttons.remove />
        </div>
      </CustomForm>
    </div>
  </CustomModal>
  <ConfirmModal ref="deleteConfirmModal" title="Suppression" :text="isRefund ? 'Supprimer ce remboursement ? L\'avoir repassera en reste à rembourser du même montant.' : 'Supprimer ce décaissement ? L\'achat repassera en reste à payer du même montant.'" yes="Supprimer" no="Retour"/>
</template>

<script>
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import ConfirmModal from "@/components/modals/confirmModal.vue";
import {NotificationService} from "@vues/v-common";
import {convertISOToDate} from "@/utils";
import {DisbursementController} from "@/purchase/controllers/DisbursementController";
import {PurchaseController} from "@/purchase/controllers/PurchaseController";
import {
  DISBURSEMENT_PAYMENT_TYPE_ENUM,
  PURCHASE_DOCUMENT_TYPE_ENUM,
  PURCHASE_STATUS_ENUM,
  enumToSelectValues,
  formatAmount,
  idOf,
  isCreditNote,
  round2
} from "@/purchase/utils";

// Modale d'un décaissement (paiement d'un achat). Sur un avoir fournisseur, c'est un remboursement reçu du fournisseur.
// - openModal({purchase}) : nouveau décaissement sur cet achat (ligne de tableau d'achats)
// - openModal() : nouveau décaissement, l'achat à payer est choisi dans la liste
// - openModal({disbursement}) : modification (ou suppression) d'un décaissement existant
// callback est appelé après chaque enregistrement ou suppression.
export default {
  name: 'DisbursementModal',
  props: ['callback'],
  components: {CustomModal, CustomForm, CustomSelectField, CustomTextField, CustomButton, ConfirmModal},
  data() {
    return {
      disbursementId: undefined,
      purchase: undefined,       // achat visé : {id, documentType, supplierName, label, reference, remainingAmount}
      maxAmount: 0,
      canChoosePurchase: false,
      payablePurchases: [],
      inputs: {
        // Obligatoire seulement quand l'achat est à choisir dans la liste (sinon le champ n'est pas affiché)
        purchaseId: {label: 'Achat à payer', value: undefined, type: 'select', selectValues: [], required: false},
        amount: {label: 'Montant TTC (€)', value: '', type: 'number', step: 0.01, required: true},
        date: {label: 'Date', value: convertISOToDate(new Date()), type: 'date', required: true},
        paymentType: {label: 'Mode de paiement', value: DISBURSEMENT_PAYMENT_TYPE_ENUM.TRANSFER.value, type: 'select', selectValues: enumToSelectValues(DISBURSEMENT_PAYMENT_TYPE_ENUM)},
        reference: {label: 'Référence (n° de chèque, de virement...)', value: '', type: 'text'},
        notes: {label: 'Notes', value: '', type: 'text'},
      },
      buttons: {
        submit: {text: "Enregistrer"},
        cancel: {text: "Annuler"},
        remove: {text: "Supprimer"},
      }
    };
  },
  computed: {
    isEdit() {
      return this.disbursementId !== undefined;
    },
    isRefund() {
      return isCreditNote(this.purchase);
    },
    title() {
      if (this.isRefund) return this.isEdit ? 'Modifier le remboursement' : 'Remboursement';
      return this.isEdit ? 'Modifier le décaissement' : 'Décaissement';
    },
    purchaseLabel() {
      if (!this.purchase) return '';
      return this.purchase.reference ? `${this.purchase.label} (${this.purchase.reference})` : this.purchase.label;
    }
  },
  watch: {
    // Choix de l'achat dans la liste : le montant proposé est son reste à payer
    "inputs.purchaseId.value"(purchaseId) {
      if (!this.canChoosePurchase) return;
      const purchase = this.payablePurchases.find(item => idOf(item) === purchaseId);
      if (!purchase) return;
      this.purchase = purchase;
      this.maxAmount = purchase.remainingAmount;
      this.inputs.amount.value = purchase.remainingAmount;
    }
  },
  methods: {
    formatAmount,
    async openModal({purchase, disbursement} = {}) {
      this.disbursementId = undefined;
      this.purchase = undefined;
      this.maxAmount = 0;
      this.canChoosePurchase = false;
      this.inputs.purchaseId.value = undefined;
      this.inputs.purchaseId.required = false;
      this.inputs.amount.value = '';
      this.inputs.amount.error = false;
      this.inputs.date.value = convertISOToDate(new Date());
      this.inputs.paymentType.value = DISBURSEMENT_PAYMENT_TYPE_ENUM.TRANSFER.value;
      this.inputs.reference.value = '';
      this.inputs.notes.value = '';
      this.$refs.modal.openModal();

      if (disbursement) {
        await this.openForEdit(disbursement);
      } else if (purchase) {
        this.purchase = purchase;
        this.maxAmount = purchase.remainingAmount;
        this.inputs.amount.value = purchase.remainingAmount;
      } else {
        await this.loadPayablePurchases();
      }
    },
    async openForEdit(disbursement) {
      this.disbursementId = idOf(disbursement);
      this.purchase = {
        id: disbursement.purchaseId,
        documentType: disbursement.isRefund ? PURCHASE_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value : PURCHASE_DOCUMENT_TYPE_ENUM.INVOICE.value,
        supplierName: disbursement.supplierName,
        label: disbursement.purchaseLabel,
        reference: disbursement.purchaseReference
      };
      this.inputs.amount.value = disbursement.amount;
      this.inputs.date.value = convertISOToDate(disbursement.date);
      this.inputs.paymentType.value = disbursement.paymentType;
      this.inputs.reference.value = disbursement.reference ?? '';
      this.inputs.notes.value = disbursement.notes ?? '';

      // Le montant maximum : ce qu'il reste à payer sur l'achat, plus ce décaissement lui-même
      this.maxAmount = disbursement.amount;
      const purchase = await PurchaseController.get(disbursement.purchaseId);
      if (purchase && !purchase.errors) {
        this.maxAmount = round2(purchase.ttcAmount - (purchase.paidAmount ?? 0) - (purchase.offsetAmount ?? 0) + disbursement.amount);
      }
    },
    // Achats sur lesquels un paiement peut être enregistré
    async loadPayablePurchases() {
      this.canChoosePurchase = true;
      this.inputs.purchaseId.required = true;
      this.payablePurchases = [];
      this.inputs.purchaseId.selectValues = [];

      const rows = await PurchaseController.getTable();
      if (!rows || rows.errors) return;

      this.payablePurchases = rows.filter(row => row.status === PURCHASE_STATUS_ENUM.TO_PAY.value || row.status === PURCHASE_STATUS_ENUM.PARTIALLY_PAID.value);
      this.inputs.purchaseId.selectValues = this.payablePurchases.map(row => ({
        label: `${row.supplierName} - ${row.reference || row.label} (${isCreditNote(row) ? 'à rembourser' : 'reste'} ${formatAmount(row.remainingAmount)})`,
        value: idOf(row)
      }));
    },
    closeModal(event = false) {
      this.$refs.modal?.closeModal(event);
    },
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    async submitForm(event) {
      this.preventNativeSubmit(event);

      if (!this.purchase) {
        this.inputs.purchaseId.error = true;
        (new NotificationService).addNotification("danger", "L'achat à payer n'est pas renseigné");
        return;
      }

      const amount = round2(Number(String(this.inputs.amount.value).replace(',', '.')));
      if (!(amount > 0)) {
        this.inputs.amount.error = true;
        (new NotificationService).addNotification("danger", "Le montant n'est pas renseigné");
        return;
      }
      if (amount > this.maxAmount) {
        this.inputs.amount.error = true;
        (new NotificationService).addNotification("danger", `Le montant dépasse ce qu'il reste à payer (${formatAmount(this.maxAmount)})`);
        return;
      }

      if (!this.inputs.date.value) {
        this.inputs.date.error = true;
        (new NotificationService).addNotification("danger", "La date n'est pas renseignée");
        return;
      }

      const body = {
        amount,
        date: this.inputs.date.value,
        paymentType: this.inputs.paymentType.value,
        reference: String(this.inputs.reference.value ?? '').trim(),
        notes: String(this.inputs.notes.value ?? '').trim(),
      };

      const res = await this.$refs.form.submitForm(() => this.isEdit
          ? DisbursementController.patch(this.disbursementId, body)
          : DisbursementController.post({...body, purchaseId: idOf(this.purchase)}));
      if (!res.errors) {
        (new NotificationService).addNotification("success", `${this.isRefund ? 'Remboursement' : 'Décaissement'} ${this.isEdit ? 'modifié' : 'enregistré'}`);
        this.closeModal(true);
        if (this.callback) this.callback();
      }
    },
    async remove() {
      const res = await this.$refs.deleteConfirmModal.displayChoice(() => DisbursementController.delete(this.disbursementId));
      if (res !== false && !(res && res.errors)) {
        (new NotificationService).addNotification("success", `${this.isRefund ? 'Remboursement' : 'Décaissement'} supprimé`);
        this.closeModal(true);
        if (this.callback) this.callback();
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.container
  display flex
  flex-direction column
  gap 10px

  form
    display flex
    flex-direction column
    gap 10px

    .recap
      display flex
      flex-direction column
      margin 0

      .recap-supplier
        font-size 1.2em
        font-weight 600
        color THEME_COLOR_PRIMARY

      .recap-purchase
        color #6b7280

    .note
      margin 0
      font-size 0.85em
      color #6b7280

    .fields-row
      display grid
      grid-template-columns 1fr 1fr
      gap 12px

    // Le bouton principal est en premier dans le DOM (action par défaut de la touche Entrée),
    // l'ordre visuel est inversé : [Annuler] [Enregistrer]
    .button-container
      display flex
      flex-direction row-reverse
      justify-content flex-start
      gap 10px
      margin-top 4px

      .button
        min-width 110px

      .remove
        margin-right auto

@media (max-width 480px)
  .container form .fields-row
    grid-template-columns 1fr
</style>

<style lang="stylus">
.disbursement-modal
  > .modal
    width 520px !important
    max-width 100%
    height auto !important
    max-height 90vh
</style>
